import { supabase } from '../lib/supabase';

/*
  User management model: DASHBOARD-MANUAL.

  After the Supabase Auth migration, creating users, deleting users, and issuing
  password/recovery links require the `service_role` key, which must never live
  in client-side code. These operations are performed in the Supabase Dashboard
  (Authentication → Users). The mutating functions below therefore surface a
  clear pointer instead of attempting a privileged operation.

  `getUsers` remains a read-only listing sourced from `profiles` (gated by RLS
  to admins/superadmins). The `currentUserEmail` parameters are accepted for
  backward compatibility with existing callers but are no longer used —
  authorization is enforced by RLS / the Dashboard.
*/

const DASHBOARD_HINT =
  'Manage users in the Supabase Dashboard → Authentication → Users.';

export const getUsers = async (_currentUserEmail?: string) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, role, username, created_at')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error loading profiles:', error);
    throw error;
  }

  // Map to the shape the Settings UI expects. Email is not exposed client-side
  // (it lives in auth.users); it is viewable in the Dashboard.
  return (data || []).map((p) => ({
    id: p.id,
    email: '',
    name: p.username || '',
    role: p.role,
  }));
};

export const createUser = async (
  _args: { email: string; name: string; role: string },
  _currentUserEmail?: string
): Promise<never> => {
  throw new Error(
    `Creating users is done in the Dashboard. Add the user under Authentication → Users, ` +
    `then set "role" (and optional "name") in their User Metadata. ${DASHBOARD_HINT}`
  );
};

export const deleteUser = async (_userId: string, _currentUserEmail?: string): Promise<never> => {
  throw new Error(`Deleting users is done in the Dashboard. ${DASHBOARD_HINT}`);
};

export const generatePasswordResetLink = async (
  _userId: string,
  _currentUserEmail?: string
): Promise<never> => {
  throw new Error(
    `Generate a password/recovery link in the Dashboard (Authentication → Users → ⋯ → ` +
    `"Send password recovery" or "Generate link"), then share it with the user. ${DASHBOARD_HINT}`
  );
};
